import React, { useState, useEffect, useCallback } from 'react';
import { X, Upload, Film, AlertCircle, CheckCircle, FolderOpen, ChevronRight, Video } from 'lucide-react';
import { adminVideoService, VideoUploadProgress } from '@/services/adminVideoService';
import { adminProductsService } from '@/services';
import { courseCurriculumService } from '@/services/courseCurriculum.service';
import { getConfig } from '@/config/Constants';
interface VideoUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadSuccess: () => void;
}

interface CourseOption {
  id: string;
  title: string;
  slug?: string;
}

interface ModuleOption {
  id: string;
  module_number: number;
  title: string;
  duration: string;
  type?: string;
}

interface LessonOption {
  id: string;
  lesson_number: number;
  title: string;
  duration: string;
  moduleId: string;
}

interface SegmentOption {
  id: string;
  segment_number: number;
  title: string;
  duration: string;
  moduleId: string;
  lessonId: string;
  hasVideo?: boolean;
  video_url?: string | null;
}

interface TopicOption {
  id: string;
  topic_number: number;
  content: string;
  segmentId: string;
  hasVideo?: boolean;
  video_url?: string | null;
}

export default function VideoUploadModal({
  isOpen,
  onClose,
  onUploadSuccess,
}: VideoUploadModalProps) {
  // Course data state
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [modules, setModules] = useState<ModuleOption[]>([]);
  const [lessons, setLessons] = useState<LessonOption[]>([]);
  const [segments, setSegments] = useState<SegmentOption[]>([]);
  const [topics, setTopics] = useState<TopicOption[]>([]);
  const [curriculum, setCurriculum] = useState<any>(null);

  // Selection state
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  const [selectedLesson, setSelectedLesson] = useState('');
  const [selectedSegment, setSelectedSegment] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');

  // Upload state
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<VideoUploadProgress | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // S3 path preview
  const [s3PathPreview, setS3PathPreview] = useState<string>('');
  const [checkingExistence, setCheckingExistence] = useState(false);
  const [videoExists, setVideoExists] = useState<boolean | null>(null);

  // Fetch courses on mount
  useEffect(() => {
    if (isOpen) {
      fetchCourses();
    }
  }, [isOpen]);

  // Fetch curriculum when course is selected
  useEffect(() => {
    if (selectedCourse) {
      fetchCurriculum(selectedCourse);
    } else {
      // Reset downstream selections
      setModules([]);
      setLessons([]);
      setSegments([]);
      setSelectedModule('');
      setSelectedLesson('');
      setSelectedSegment('');
      setCurriculum(null);
    }
  }, [selectedCourse]);

  // Update lessons when module is selected
  useEffect(() => {
    if (selectedModule && curriculum) {
      const module = curriculum.course?.modules?.find((m: any) => m.id === selectedModule);
      if (module && module.lessons) {
        setLessons(module.lessons.map((lesson: any) => ({
          id: lesson.id,
          lesson_number: lesson.lesson_number,
          title: lesson.title,
          duration: lesson.duration,
          moduleId: module.id,
        })));
      }
    } else {
      setLessons([]);
      setSelectedLesson('');
      setSegments([]);
      setSelectedSegment('');
    }
  }, [selectedModule, curriculum]);

  // Update segments when lesson is selected
  useEffect(() => {
    if (selectedLesson && curriculum) {
      const module = curriculum.course?.modules?.find((m: any) => m.id === selectedModule);
      const lesson = module?.lessons?.find((l: any) => l.id === selectedLesson);
      if (lesson && lesson.segments) {
        setSegments(lesson.segments.map((segment: any) => ({
          id: segment.id,
          segment_number: segment.segment_number,
          title: segment.title,
          duration: segment.duration,
          moduleId: module.id,
          lessonId: lesson.id,
          hasVideo: !!segment.video_url,
          video_url: segment.video_url,
        })));
      }
    } else {
      setSegments([]);
      setSelectedSegment('');
    }
  }, [selectedLesson, selectedModule, curriculum]);

  // Update topics when segment is selected
  useEffect(() => {
    if (selectedSegment && curriculum) {
      const module = curriculum.course?.modules?.find((m: any) => m.id === selectedModule);
      const lesson = module?.lessons?.find((l: any) => l.id === selectedLesson);
      const segment = lesson?.segments?.find((s: any) => s.id === selectedSegment);
      if (segment && segment.topics) {
        setTopics(segment.topics.map((topic: any) => ({
          id: topic.id,
          topic_number: topic.topic_number,
          content: topic.content,
          segmentId: segment.id,
          hasVideo: !!topic.video_url,
          video_url: topic.video_url,
        })));
      }
    } else {
      setTopics([]);
      setSelectedTopic('');
    }
  }, [selectedSegment, selectedModule, selectedLesson, curriculum]);

  // Update S3 path preview and check existence
  useEffect(() => {
    if (selectedCourse && selectedTopic && curriculum) {
      const courseSlug = curriculum.course?.slug || '';
      // Extract module, lesson, segment from topic ID (e.g., "1.1.1.1")
      const parts = selectedTopic.split('.');
      if (parts.length >= 4) {
        const moduleId = parts[0];
        const lessonId = `${parts[0]}.${parts[1]}`;
        const segmentId = `${parts[0]}.${parts[1]}.${parts[2]}`;
        const topicId = selectedTopic;
        const path = `${courseSlug}/${moduleId}/${lessonId}/${segmentId}/${topicId}/video.mp4`;
        setS3PathPreview(path);
        checkVideoExistence(selectedCourse, selectedTopic);
      }
    } else {
      setS3PathPreview('');
      setVideoExists(null);
    }
  }, [selectedCourse, selectedTopic, curriculum]);

  const fetchCourses = async () => {
    try {
      const response = await adminProductsService.fetchCourses();
      if (response.data) {
        setCourses(
          response.data.map(course => ({
            id: course.id,
            title: course.title,
          }))
        );
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      setError('Failed to load courses');
    }
  };

  const fetchCurriculum = async (courseId: string) => {
    try {
      // Use the proper service with correct authentication
      const response = await courseCurriculumService.getCourseCurriculumByCourseId(courseId);

      if (response.success && response.data) {
        const curriculumData = response.data;
        setCurriculum(curriculumData);

        // Extract modules from the curriculum structure
        if (curriculumData.course?.modules) {
          setModules(curriculumData.course.modules.map((module: any) => ({
            id: module.id,
            module_number: module.module_number,
            title: module.title,
            duration: module.duration,
            type: module.type,
          })));
        } else {
          setModules([]);
        }
      } else {
        console.error('Failed to fetch curriculum:', response.error);
        setError(response.error || 'Failed to load course structure');
        setModules([]);
      }
    } catch (error) {
      console.error('Error fetching curriculum:', error);
      setError('Failed to load course structure');
      setModules([]);
    }
  };

  const checkVideoExistence = async (courseId: string, topicId: string) => {
    setCheckingExistence(true);
    try {
      const config = getConfig();
      const response = await fetch(`${config.apiUrl}/videos/check-video`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({
          course_id: courseId,
          topic_id: topicId,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setVideoExists(data.exists);
      }
    } catch (error) {
      console.error('Error checking video existence:', error);
    } finally {
      setCheckingExistence(false);
    }
  };

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, []);

  const handleFileSelect = (file: File) => {
    const validation = adminVideoService.validateVideoFile(file);
    if (!validation.valid) {
      setError(validation.error || 'Invalid file');
      return;
    }

    setVideoFile(file);
    setError(null);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedCourse || !selectedModule || !selectedLesson || !selectedSegment || !selectedTopic || !videoFile) {
      setError('Please complete all selections and choose a video file');
      return;
    }

    setIsUploading(true);
    setError(null);
    setSuccess(false);
    setUploadStatus('Preparing upload...');

    try {
      const response = await adminVideoService.uploadVideoPresigned(
        {
          courseId: selectedCourse,
          segmentId: selectedSegment,
          topicId: selectedTopic,
          title,
          description,
          file: videoFile,
        },
        (progress) => {
          setUploadProgress(progress);
        },
        (status) => {
          setUploadStatus(status);
        }
      );

      if (response.success) {
        setSuccess(true);
        setUploadProgress(null);
        setUploadStatus('Upload complete!');
        setTimeout(() => {
          onUploadSuccess();
          handleClose();
        }, 2000);
      }
    } catch (error: any) {
      setError(error.message || 'Upload failed');
      setUploadProgress(null);
      setUploadStatus('');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedCourse('');
      setSelectedModule('');
      setSelectedLesson('');
      setSelectedSegment('');
      setSelectedTopic('');
      setVideoFile(null);
      setTitle('');
      setDescription('');
      setError(null);
      setSuccess(false);
      setUploadProgress(null);
      setUploadStatus('');
      setS3PathPreview('');
      setVideoExists(null);
      onClose();
    }
  };

  const getSegmentDisplayTitle = (segment: SegmentOption) => {
    return `${segment.id} - ${segment.title} (${segment.duration})`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-purple-500/30 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-600/20 rounded-lg">
              <Film className="w-5 h-5 text-purple-400" />
            </div>
            <h2 className="text-xl font-bold">Upload Video</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isUploading}
            className="text-gray-400 hover:text-white transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Breadcrumb */}
        {selectedCourse && (
          <div className="px-6 pt-4">
            <div className="flex items-center gap-2 text-sm text-gray-400 flex-wrap">
              <FolderOpen className="w-4 h-4" />
              {courses.find(c => c.id === selectedCourse)?.title}
              {selectedModule && (
                <>
                  <ChevronRight className="w-4 h-4" />
                  <span className="text-gray-300">
                    Module {modules.find(m => m.id === selectedModule)?.module_number}
                  </span>
                </>
              )}
              {selectedLesson && (
                <>
                  <ChevronRight className="w-4 h-4" />
                  <span className="text-gray-300">
                    Lesson {lessons.find(l => l.id === selectedLesson)?.lesson_number}
                  </span>
                </>
              )}
              {selectedSegment && (
                <>
                  <ChevronRight className="w-4 h-4" />
                  <span className="text-white font-medium">
                    Segment {selectedSegment}
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Course Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              1. Select Course *
            </label>
            <select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              disabled={isUploading}
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white focus:border-purple-500 focus:outline-none disabled:opacity-50"
            >
              <option value="">Choose a course...</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.title}
                </option>
              ))}
            </select>
          </div>

          {/* Module Selection - CRITICAL FIX: Removed modules.length > 0 check */}
          {selectedCourse && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                2. Select Module *
              </label>
              <select
                value={selectedModule}
                onChange={(e) => setSelectedModule(e.target.value)}
                disabled={isUploading}
                className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white focus:border-purple-500 focus:outline-none disabled:opacity-50"
              >
                <option value="">
                  {modules.length === 0 ? "Loading modules..." : "Choose a module..."}
                </option>
                {modules.map((module) => (
                  <option key={module.id} value={module.id}>
                    Module {module.module_number}: {module.title} ({module.duration})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Lesson Selection - CRITICAL FIX: Removed lessons.length > 0 check */}
          {selectedModule && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                3. Select Lesson *
              </label>
              <select
                value={selectedLesson}
                onChange={(e) => setSelectedLesson(e.target.value)}
                disabled={isUploading}
                className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white focus:border-purple-500 focus:outline-none disabled:opacity-50"
              >
                <option value="">
                  {lessons.length === 0 ? "Loading lessons..." : "Choose a lesson..."}
                </option>
                {lessons.map((lesson) => (
                  <option key={lesson.id} value={lesson.id}>
                    Lesson {lesson.lesson_number}: {lesson.title} ({lesson.duration})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Segment Selection - CRITICAL FIX: Removed segments.length > 0 check */}
          {selectedLesson && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                4. Select Segment *
              </label>
              <select
                value={selectedSegment}
                onChange={(e) => setSelectedSegment(e.target.value)}
                disabled={isUploading}
                className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white focus:border-purple-500 focus:outline-none disabled:opacity-50"
              >
                <option value="">
                  {segments.length === 0 ? "Loading segments..." : "Choose a segment..."}
                </option>
                {segments.map((segment) => (
                  <option key={segment.id} value={segment.id}>
                    {segment.hasVideo && '✅ '}{getSegmentDisplayTitle(segment)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Topic Selection - NEW: 5th level for topic-level videos */}
          {selectedSegment && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                5. Select Topic *
              </label>
              <select
                value={selectedTopic}
                onChange={(e) => setSelectedTopic(e.target.value)}
                disabled={isUploading}
                className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white focus:border-purple-500 focus:outline-none disabled:opacity-50"
              >
                <option value="">
                  {topics.length === 0 ? "Loading topics..." : "Choose a topic..."}
                </option>
                {topics.map((topic) => (
                  <option key={topic.id} value={topic.id}>
                    {topic.hasVideo && '✅ '}{topic.id} - {topic.content.substring(0, 80)}...
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* S3 Path Preview */}
          {s3PathPreview && (
            <div className="p-4 bg-black/60 rounded-lg border border-purple-500/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-400 font-medium">UPLOAD DESTINATION</span>
                {checkingExistence ? (
                  <span className="text-xs text-gray-400">Checking...</span>
                ) : videoExists !== null && (
                  <span className={`text-xs font-medium ${videoExists ? 'text-yellow-400' : 'text-green-400'}`}>
                    {videoExists ? '⚠️ Video exists (will be replaced)' : '✓ Location available'}
                  </span>
                )}
              </div>
              <code className="text-sm text-purple-300 font-mono break-all">
                s3://aiacademy-course-videos/{s3PathPreview}
              </code>
            </div>
          )}

          {/* Video File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              6. Video File *
            </label>
            <div
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all ${
                isDragging
                  ? 'border-purple-500 bg-purple-500/10'
                  : 'border-white/20 hover:border-white/30'
              } ${isUploading ? 'pointer-events-none opacity-50' : ''}`}
            >
              {videoFile ? (
                <div className="space-y-2">
                  <Film className="w-12 h-12 text-purple-400 mx-auto" />
                  <p className="text-white font-medium">{videoFile.name}</p>
                  <p className="text-gray-400 text-sm">
                    {adminVideoService.formatFileSize(videoFile.size)}
                  </p>
                  <button
                    onClick={() => setVideoFile(null)}
                    disabled={isUploading}
                    className="text-purple-400 hover:text-purple-300 text-sm underline"
                  >
                    Remove file
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                  <div>
                    <p className="text-gray-300">
                      Drag and drop your video here, or
                    </p>
                    <label className="inline-block mt-2">
                      <input
                        type="file"
                        accept="video/*"
                        onChange={handleFileInput}
                        disabled={isUploading}
                        className="hidden"
                      />
                      <span className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg cursor-pointer inline-block transition-colors">
                        Browse Files
                      </span>
                    </label>
                  </div>
                  <p className="text-gray-500 text-xs">
                    Supported formats: MP4, WebM, MOV, AVI (Max 2GB)
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Optional Title */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Video Title (Optional)
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isUploading}
              placeholder="Enter a descriptive title"
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none disabled:opacity-50"
            />
          </div>

          {/* Optional Description */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isUploading}
              placeholder="Enter video description"
              rows={3}
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none disabled:opacity-50 resize-none"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-3 p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="flex items-start gap-3 p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <p className="text-green-400 text-sm">
                Video uploaded successfully!
              </p>
            </div>
          )}

          {/* Upload Progress */}
          {uploadProgress && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">{uploadStatus || 'Uploading...'}</span>
                <span className="text-purple-400">{uploadProgress.percentage}%</span>
              </div>
              <div className="w-full bg-black/40 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-purple-600 to-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress.percentage}%` }}
                />
              </div>
              <p className="text-xs text-gray-500">
                {adminVideoService.formatFileSize(uploadProgress.loaded)} of{' '}
                {adminVideoService.formatFileSize(uploadProgress.total)}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              disabled={isUploading}
              className="flex-1 px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={!selectedCourse || !selectedModule || !selectedLesson || !selectedSegment || !selectedTopic || !videoFile || isUploading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isUploading ? (
                <>Uploading...</>
              ) : (
                <>
                  <Video className="w-4 h-4" />
                  Upload Video
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}